import {
  CheckCircle,
  Compass,
  Info,
  LinkedinLogo,
  Plugs,
  Warning,
} from "@phosphor-icons/react";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import ProfileImage from "common/components/ui/ProfileImage";
import { useCampaignContext } from "common/helpers/CampaignContext";
import useAvailableLicensesCount from "common/hooks/useAvailableLicensesCount";
import type { WorkspaceAccount } from "common/types";

import type React from "react";

interface WorkerAccountProps {
  account: WorkspaceAccount;
  openModal: (
    type: "reconnect" | "license" | "assignSeat",
    account: WorkspaceAccount,
  ) => void;
}

export default function WorkerAccount({
  account,
  openModal,
}: WorkerAccountProps) {
  const {
    full_name: fullName,
    picture_url: pictureUrl,
    premium: isPremium,
    sales_navigator: isSalesNavigator,
    state,
    type,
    license,
  } = account;

  const { availableLicensesCount } = useAvailableLicensesCount();

  const { uses_connection_note: isUsingNote, state: campaignState } =
    useCampaignContext();

  const isDraft = campaignState === "INIT";
  const isLoggedIn = state === "LoggedIn";

  let label: string;
  let actionComponent: React.ReactNode;

  if (type === "user" && !license) {
    label = "No seat";
    actionComponent = (
      <Button
        variant="link-purple"
        size="sm"
        onClick={() =>
          openModal(availableLicensesCount ? "assignSeat" : "license", account)
        }
      >
        Assign seat
      </Button>
    );
  } else if (!isLoggedIn) {
    label = "Disconnected";
    actionComponent = (
      <Button
        variant="link-black"
        size="sm"
        onClick={() => openModal("reconnect", account)}
      >
        Reconnect
      </Button>
    );
  } else if (isUsingNote && !isPremium) {
    label = "No LinkedIn premium";
    actionComponent = (
      <span className="flex items-center gap-x-1 px-1 text-button-xs text-black-400">
        Upgrade <Info weight="fill" size={16} className="-mr-1" />
      </span>
    );
  }

  const isError = !!label;

  return (
    <div
      className={clsx(
        "flex h-10 items-center gap-x-1.5 rounded-xl p-2",
        isError ? "bg-black-100" : "bg-white",
      )}
    >
      <RenderIf condition={!isDraft}>
        {isError ? (
          <Warning weight="fill" size={16} className="fill-black-400" />
        ) : (
          <CheckCircle weight="fill" size={16} className="fill-green-400" />
        )}
      </RenderIf>

      <ProfileImage src={pictureUrl} size="xs" alt={fullName} />

      <div className="flex flex-1 flex-col">
        <div className="flex items-center gap-x-1">
          <span
            className={clsx(
              "line-clamp-1 break-all text-sm-medium",
              isError && "text-black-400",
            )}
          >
            {fullName}
          </span>

          <RenderIf condition={isDraft}>
            {isLoggedIn ? (
              <>
                <RenderIf condition={isPremium}>
                  <LinkedinLogo
                    size={16}
                    weight="fill"
                    className="fill-yellow-500"
                  />
                </RenderIf>

                <RenderIf condition={isSalesNavigator}>
                  <Compass size={16} weight="fill" className="fill-blue-500" />
                </RenderIf>
              </>
            ) : (
              <Plugs weight="fill" className="fill-black-400" size={16} />
            )}
          </RenderIf>
        </div>

        <RenderIf condition={isError}>
          <span className="-mt-0.5 text-button-xxs text-black-400">
            {label}
          </span>
        </RenderIf>
      </div>

      {actionComponent}
    </div>
  );
}
